/**
 * AuthProvider Component
 * Provides authentication context to the application
 * Integrates with Zustand store for state management
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import {
  initOktaAuth,
  signIn as oktaSignIn,
  signOut as oktaSignOut,
  verifyMFA as oktaVerifyMFA,
  getUserInfo,
  refreshTokens,
  isAuthenticated as oktaIsAuthenticated,
  forgotPassword as oktaForgotPassword,
  sendInitialPasscode,
  confirmPasscode,
} from "../okta";
import { tokenStorage } from "../storage";
import { useAuthStore } from "../store";
import { decodeJWT } from "../utils/jwt";
import type {
  AuthConfig,
  AuthState,
  LoginCredentials,
  MFAVerifyRequest,
  PasswordResetRequest,
  MFAFactor,
  MFAState,
} from "../types";
import { authReducer, initialAuthState } from "./authReducer";
import { resolveLoginMfaExpiry, pickLoginMfaExpiryBase } from "./authMfaExpiry";

export interface AuthContextValue extends AuthState {
  /** Sign in with credentials */
  signIn: (credentials: LoginCredentials) => Promise<{ success: boolean; tokens?: any; mfaState?: any; status?: string }>;
  /** Sign out */
  signOut: () => Promise<void>;
  /** Verify MFA */
  verifyMFA: (request: MFAVerifyRequest) => Promise<void>;
  /** Select MFA factor */
  selectMFAFactor: (factorId: string) => Promise<void>;
  /** Resend MFA code */
  resendMFACode: (factorId: string) => Promise<void>;
  /** Initiate password reset */
  forgotPassword: (request: PasswordResetRequest) => Promise<void>;
  /** Refresh tokens */
  refreshTokens: () => Promise<void>;
  /** Clear auth error */
  clearError: () => void;
  /** Clear MFA state */
  clearMFAState: () => void;
  /** Check auth status */
  checkAuthStatus: () => Promise<void>;
}

function mergeMfaChallengePayload(
  base: MFAState,
  updates: {
    stateToken: string;
    stateTokenExpiresAt?: number;
    factors?: MFAFactor[];
    oktaId?: string;
  },
): MFAState {
  return {
    ...base,
    factors: updates.factors ?? base.factors,
    stateToken: updates.stateToken,
    stateTokenExpiresAt: updates.stateTokenExpiresAt ?? base.stateTokenExpiresAt,
    oktaId: updates.oktaId ?? base.oktaId,
    status: "MFA_CHALLENGE",
  };
}

function toMfaStorePayload(mfaState: MFAState) {
  return {
    factors: mfaState.factors,
    stateToken: mfaState.stateToken,
    stateTokenExpiresAt: mfaState.stateTokenExpiresAt,
    oktaId: mfaState.oktaId,
  };
}

function toInitialLoginMfaStorePayload(mfaState: MFAState) {
  return {
    ...toMfaStorePayload(mfaState),
    loginMfaAnchorExpiresAt: mfaState.stateTokenExpiresAt,
  };
}

function resolveLoginMfaChallengeExpiry(
  contextExpiresAt: number | undefined,
  updateExpiresAt: number | undefined,
  storeSnapshot: ReturnType<typeof useAuthStore.getState>,
): number | undefined {
  const loginAnchor = storeSnapshot.loginMfaAnchorExpiresAt;
  const baseExpiresAt = pickLoginMfaExpiryBase(
    contextExpiresAt,
    storeSnapshot.stateTokenExpiresAt,
    loginAnchor,
  );

  return resolveLoginMfaExpiry(
    baseExpiresAt,
    updateExpiresAt,
    loginAnchor,
  );
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  /** Okta configuration */
  config: AuthConfig;
  /** Children components */
  children: ReactNode;
  /** Callback when auth state changes */
  onAuthStateChange?: (state: AuthState) => void;
}

export function AuthProvider({
  config,
  children,
  onAuthStateChange,
}: Readonly<AuthProviderProps>) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const authStore = useAuthStore();

  // Check initial auth status
  const checkAuthStatus = useCallback(async () => {
    try {
      const authenticated = await oktaIsAuthenticated();
      if (authenticated) {
        const tokens = await tokenStorage.getTokens();
        const user = await getUserInfo();
        if (tokens && user) {
          dispatch({
            type: "AUTH_SUCCESS",
            payload: { user, tokens },
          });
          authStore.setLoggedIn({ user, tokens });
          return;
        }
      }
      dispatch({ type: "AUTH_LOGOUT" });
      authStore.resetUser();
    } catch (error) {
      console.error("Auth status check failed:", error);
      dispatch({ type: "AUTH_LOGOUT" });
      authStore.resetUser();
    }
  }, [authStore]);

  // Initialize Okta on mount
  useEffect(() => {
    initOktaAuth(config);
    // No auto session verification unless explicitly triggered
  }, [config]);

  // Notify parent of auth state changes
  useEffect(() => {
    onAuthStateChange?.(state);
  }, [state, onAuthStateChange]);

  // Helper to get user info with fallback
  const getOrCreateUserInfo = useCallback(async (
    accessToken: string,
    username: string
  ) => {
    let user = await getUserInfo();
    
    if (user) {
      return user;
    }

    // Fallback: Decode access token to get basic user info
    const payload = decodeJWT(accessToken);
    if (payload?.sub) {
      return {
        sub: payload.sub,
        email: payload.email ?? username,
      };
    }

    // Final fallback if token decode fails
    return {
      sub: username,
      email: username,
    };
  }, []);

  // Sign in handler
  const handleSignIn = useCallback(async (credentials: LoginCredentials) => {
    dispatch({ type: "AUTH_LOADING" });
    authStore.setLoading(true);

    try {
      console.log("🔐 [AuthProvider] Calling oktaSignIn...");
      const result = await oktaSignIn(credentials);
      console.log("🔐 [AuthProvider] oktaSignIn result:", { success: result.success, hasTokens: !!result.tokens, hasMfaState: !!result.mfaState });

      if (result.success && result.tokens) {
        console.log("🔐 [AuthProvider] Success! Getting user info...");
        const user = await getOrCreateUserInfo(result.tokens.accessToken, credentials.username);
        console.log("🔐 [AuthProvider] User info retrieved:", user ? "✓" : "✗ (using fallback)");
        
        console.log("🔐 [AuthProvider] Dispatching AUTH_SUCCESS and updating store...");
        dispatch({
          type: "AUTH_SUCCESS",
          payload: { user, tokens: result.tokens },
        });
        // Update Zustand store
        authStore.setLoggedIn({ user, tokens: result.tokens });
        console.log("🔐 [AuthProvider] Login complete! User is authenticated.");
        return result;
      } else if (result.mfaState) {
        // Dispatch appropriate action based on MFA state status
        const actionType = result.mfaState.status === "MFA_CHALLENGE" 
          ? "AUTH_MFA_CHALLENGE" 
          : "AUTH_MFA_REQUIRED";
        
        dispatch({
          type: actionType,
          payload: result.mfaState,
        });
        // Update Zustand store with MFA factors
        authStore.setMfaFactors(toInitialLoginMfaStorePayload(result.mfaState));
        return result;
      } else if (result.status === "LOCKED_OUT") {
        const lockedMessage = "Account locked out";
        dispatch({
          type: "AUTH_ERROR",
          payload: {
            code: "E0000069",
            message: lockedMessage,
            originalError: result,
          },
        });
        authStore.setError(lockedMessage);
        authStore.setLoading(false);
        return result;
      }

      authStore.setLoading(false);
      return { success: false };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Authentication failed";
      dispatch({
        type: "AUTH_ERROR",
        payload: {
          code: "AUTH_FAILED",
          message: errorMessage,
          originalError: error,
        },
      });
      authStore.setError(errorMessage);
      authStore.setLoading(false);
      return { success: false };
    }
  }, [authStore]);

  // Sign out handler
  const handleSignOut = useCallback(async () => {
    dispatch({ type: "AUTH_LOADING" });
    authStore.setLoading(true);

    try {
      await oktaSignOut();
      dispatch({ type: "AUTH_LOGOUT" });
      // Update Zustand store
      await authStore.logout();
    } catch (error) {
      console.error("Sign out error:", error);
      dispatch({ type: "AUTH_LOGOUT" });
      await authStore.logout();
    }
  }, [authStore]);

  // MFA verification handler
  // Helper: create minimal user from token
  const createMinimalUser = (token: string) => {
    const payload = decodeJWT(token);
    if (payload?.sub) {
      return {
        sub: payload.sub,
        email: payload.email ?? payload.preferred_username ?? 'user@example.com',
        name: payload.name ?? payload.email ?? 'User',
      };
    }
    return {
      sub: 'user',
      email: 'user@example.com',
      name: 'User',
    };
  };

  // Helper: handle MFA state
  const handleMFAState = (mfaState: MFAState) => {
    const actionType = mfaState.status === "MFA_CHALLENGE" ? "AUTH_MFA_CHALLENGE" : "AUTH_MFA_REQUIRED";
    dispatch({ type: actionType, payload: mfaState });
    authStore.setMfaFactors(toMfaStorePayload(mfaState));
  };

  // Helper: handle MFA error
  const handleMFAError = (error: unknown) => {
    const err = error as Error & {
      errorCode?: string;
      stateToken?: string;
      stateTokenExpiresAt?: number;
    };
    const errorMessage = err instanceof Error ? err.message : "MFA verification failed";
    const errorCode = err?.errorCode;
    if (err?.stateToken && state.mfaState) {
      console.log("🔑 Preserving mfaState with new stateToken from error response");
      const nextMfaState = mergeMfaChallengePayload(state.mfaState, {
        stateToken: err.stateToken,
      });
      dispatch({
        type: "AUTH_MFA_CHALLENGE",
        payload: nextMfaState,
      });
      authStore.setMfaFactors(toMfaStorePayload(nextMfaState));
    }
    dispatch({
      type: "AUTH_ERROR",
      payload: {
        code: errorCode ?? "MFA_FAILED",
        message: errorMessage,
        originalError: error,
      },
    });
    authStore.setError(errorMessage);
    authStore.setLoading(false);
    throw error;
  };

  const handleVerifyMFA = useCallback(async (request: MFAVerifyRequest) => {
    dispatch({ type: "AUTH_LOADING" });
    authStore.setLoading(true);
    try {
      if (request.link && request.passCode) {
        console.log('🔐 [AuthProvider] Verifying MFA with confirmPasscode...');
        const result = await confirmPasscode(request.link, request.stateToken, request.passCode);
        console.log('🔐 [AuthProvider] confirmPasscode returned:', { 
          status: result.status, 
          hasSessionToken: !!result.sessionToken,
          hasAccessToken: !!result.accessToken,
        });
        
        // Verify sessionToken was stored
        const storedToken = sessionStorage.getItem('okta_session_token');
        console.log('🔐 [AuthProvider] Checking if sessionToken stored:', { 
          stored: !!storedToken,
          length: storedToken?.length,
        });
        
        if (result.status === "SUCCESS" && (result.accessToken || result.sessionToken)) {
          const sessionOrAccessToken = result.accessToken ?? result.sessionToken;
          const tokens = {
            accessToken: sessionOrAccessToken,
            idToken: sessionOrAccessToken,
            expiresAt: Math.floor(Date.now() / 1000) + 3600,
            tokenType: "Bearer",
          };
          let user = await getUserInfo();
          if (!user) {
            user = createMinimalUser(sessionOrAccessToken);
          }
          console.log('🔐 [AuthProvider] Dispatching AUTH_SUCCESS');
          dispatch({ type: "AUTH_SUCCESS", payload: { user, tokens } });
          authStore.setLoggedIn({ user, tokens });
          console.log('🔐 [AuthProvider] MFA verification complete');
          return;
        } else {
          throw new Error(`MFA verification failed: ${result.status}`);
        }
      }
      const result = await oktaVerifyMFA(request);
      if (result.success && result.tokens) {
        const user = await getUserInfo();
        if (user) {
          dispatch({ type: "AUTH_SUCCESS", payload: { user, tokens: result.tokens } });
          authStore.setLoggedIn({ user, tokens: result.tokens });
        }
      } else if (result.mfaState) {
        handleMFAState(result.mfaState);
      }
    } catch (error) {
      console.error('🔐 [AuthProvider] MFA verification error:', error);
      handleMFAError(error);
    }
  }, [authStore, state.mfaState]);

  // MFA factor selection handler
  const handleSelectMFAFactor = useCallback(
    async (factorId: string) => {
      if (!state.mfaState) return;

      dispatch({ type: "AUTH_LOADING" });
      authStore.setLoading(true);

      try {
        // Find the selected factor to get its verify link
        const selectedFactor = state.mfaState.factors.find(
          (factor) => factor.id === factorId,
        );

        if (!selectedFactor) {
          throw new Error("Selected MFA factor not found");
        }

        if (!selectedFactor.link) {
          throw new Error("MFA factor verify link not available");
        }

        // Trigger OTP using factor's verify link (matching mobile app pattern)
        const result = await sendInitialPasscode(
          selectedFactor.link,
          state.mfaState.stateToken,
        );

        console.log("🔑 Updating stateToken after factor selection");
        const storeSnapshot = useAuthStore.getState();
        const nextMfaState = mergeMfaChallengePayload(state.mfaState, {
          stateToken: result.stateToken,
          stateTokenExpiresAt: resolveLoginMfaChallengeExpiry(
            state.mfaState.stateTokenExpiresAt,
            result.stateTokenExpiresAt,
            storeSnapshot,
          ),
          oktaId: result.oktaId,
        });
        authStore.setMfaFactors(toMfaStorePayload(nextMfaState));

        dispatch({
          type: "AUTH_MFA_CHALLENGE",
          payload: nextMfaState,
        });

        // Update selected factor in Zustand store
        authStore.setNewSelectedFactor(factorId);
        authStore.setLoading(false);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to select MFA factor";
        dispatch({
          type: "AUTH_ERROR",
          payload: {
            code: "MFA_SELECT_FAILED",
            message: errorMessage,
            originalError: error,
          },
        });
        authStore.setError(errorMessage);
        authStore.setLoading(false);
      }
    },
    [state.mfaState, authStore],
  );

  // Resend MFA code handler (re-trigger OTP)
  const handleResendMFACode = useCallback(
    async (factorId: string) => {
      const storeSnapshot = useAuthStore.getState();
      const { mfaFactors, stateTokenExpiresAt } = storeSnapshot;
      const stateToken = storeSnapshot.stateToken || state.mfaState?.stateToken;
      if (!stateToken) {
        throw new Error("MFA state token not available");
      }
      if (!mfaFactors || mfaFactors.length === 0) {
        throw new Error("MFA factors not available");
      }
      try {
        // Find the selected factor to get its verify link
        const selectedFactor = mfaFactors.find(
          (factor: MFAFactor) => factor.id === factorId,
        );
        if (!selectedFactor) {
          throw new Error("Selected MFA factor not found");
        }
        if (!selectedFactor.link) {
          throw new Error("MFA factor verify link not available");
        }
        // Re-trigger OTP using factor's verify link (matching mobile app pattern)
        const result = await sendInitialPasscode(
          selectedFactor.link,
          stateToken,
        );

        console.log("🔑 Updating stateToken after resend");
        const baseMfaState: MFAState = {
          factors: mfaFactors,
          stateToken,
          stateTokenExpiresAt,
          oktaId: state.mfaState?.oktaId ?? storeSnapshot.oktaId,
          status: "MFA_CHALLENGE",
        };
        const nextMfaState = mergeMfaChallengePayload(baseMfaState, {
          stateToken: result.stateToken,
          stateTokenExpiresAt: resolveLoginMfaChallengeExpiry(
            state.mfaState?.stateTokenExpiresAt ?? baseMfaState.stateTokenExpiresAt,
            result.stateTokenExpiresAt,
            storeSnapshot,
          ),
          oktaId: result.oktaId,
        });
        authStore.setMfaFactors(toMfaStorePayload(nextMfaState));
        dispatch({
          type: "AUTH_MFA_CHALLENGE",
          payload: nextMfaState,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to resend MFA code";
        dispatch({
          type: "AUTH_ERROR",
          payload: {
            code: "MFA_RESEND_FAILED",
            message: errorMessage,
            originalError: error,
          },
        });
        authStore.setError(errorMessage);
        authStore.setLoading(false);
      }
    },
    [authStore, state.mfaState],
  );

  // Forgot password handler
  const handleForgotPassword = useCallback(
    async (request: PasswordResetRequest) => {
      dispatch({ type: "AUTH_LOADING" });

      try {
        await oktaForgotPassword(request);
        dispatch({ type: "AUTH_CLEAR_ERROR" });
      } catch (error) {
        dispatch({
          type: "AUTH_ERROR",
          payload: {
            code: "PASSWORD_RESET_FAILED",
            message:
              error instanceof Error ? error.message : "Password reset failed",
            originalError: error,
          },
        });
      }
    },
    [],
  );

  // Token refresh handler
  const handleRefreshTokens = useCallback(async () => {
    try {
      const tokens = await refreshTokens();
      if (tokens) {
        dispatch({
          type: "AUTH_TOKEN_REFRESH",
          payload: tokens,
        });
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      dispatch({ type: "AUTH_LOGOUT" });
    }
  }, []);

  // Clear error handler
  const handleClearError = useCallback(() => {
    dispatch({ type: "AUTH_CLEAR_ERROR" });
  }, []);

  // Clear MFA state handler
  const handleClearMFAState = useCallback(() => {
    dispatch({ type: "AUTH_CLEAR_MFA_STATE" });
    authStore.clearMfaState();
  }, [authStore]);

  const value = useMemo<AuthContextValue & { checkAuthStatus: () => Promise<void> }>(
    () => ({
      ...state,
      signIn: handleSignIn,
      signOut: handleSignOut,
      verifyMFA: handleVerifyMFA,
      selectMFAFactor: handleSelectMFAFactor,
      resendMFACode: handleResendMFACode,
      forgotPassword: handleForgotPassword,
      refreshTokens: handleRefreshTokens,
      clearError: handleClearError,
      clearMFAState: handleClearMFAState,
      checkAuthStatus, // Expose manual session verification
    }),
    [
      state,
      handleSignIn,
      handleSignOut,
      handleVerifyMFA,
      handleSelectMFAFactor,
      handleResendMFACode,
      handleForgotPassword,
      handleRefreshTokens,
      handleClearError,
      handleClearMFAState,
      checkAuthStatus,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
